import { useState, useRef, useEffect } from 'react';
import { Bell, CheckCheck, Megaphone, User } from 'lucide-react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { toDate } from '@elka/utils';
import { useCrmNotifications } from '../hooks/useCrmNotifications';
import { useAuth } from '@/features/auth/hooks/useAuth';

export function NotificationDropdown() {
  const { user } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useCrmNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Bail when auth hasn't resolved — otherwise `user?.uid || ''` makes the empty-string
  // user look "unread to" every notification and the badge count is briefly wrong.
  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="border-line text-ink-2 rounded-control hover:bg-surface-soft relative flex h-8 w-8 items-center justify-center border transition-[color,background-color,transform] after:absolute after:inset-[-4px] after:content-[''] active:scale-[0.96]"
        title="הודעות"
      >
        <Bell size={18} />
        {/* One signal: a static danger dot. Show a count bubble (and pulse) only
            past 5 unread — the "always urgent" badge trained users to ignore it. */}
        {unreadCount > 5 ? (
          <span className="bg-danger absolute -top-1 -right-1 z-10 flex h-[18px] min-w-[18px] animate-pulse items-center justify-center rounded-full px-1 text-[10px] font-bold text-white tabular-nums">
            {unreadCount}
          </span>
        ) : unreadCount > 0 ? (
          <span className="bg-danger absolute top-1 right-1 h-1.5 w-1.5 rounded-full" />
        ) : null}
      </button>

      {isOpen && (
        <div className="bg-surface text-ink rounded-card fixed inset-x-4 top-16 z-50 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.06),var(--shadow-overlay)] sm:absolute sm:inset-x-auto sm:top-auto sm:left-0 sm:mt-2 sm:w-96">
          {/* Header */}
          <div className="border-line flex items-center justify-between border-b px-4 py-3">
            <h3 className="text-ink font-bold text-balance">הודעות</h3>
            {unreadCount > 0 && (
              <button
                onClick={() => {
                  // Fire-and-forget from a click handler — guard so a rejected write
                  // surfaces in the console instead of becoming an unhandled rejection.
                  void markAllAsRead().catch((err) =>
                    console.error('Failed to mark all notifications as read:', err)
                  );
                }}
                className="text-brand hover:text-brand-ink -m-2 flex min-h-10 items-center gap-1 p-2 text-xs font-medium transition-[color,transform] active:scale-[0.96]"
              >
                <CheckCheck size={14} />
                סמן הכל כנקרא
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center px-4 py-10 text-center">
                <div className="bg-surface-soft border-line mb-3 flex h-12 w-12 items-center justify-center rounded-full border">
                  <Bell size={22} className="text-muted" />
                </div>
                <p className="text-muted text-sm font-medium">אין הודעות חדשות</p>
                <p className="text-sub mt-1 text-xs">כשתהיינה הודעות, הן יופיעו כאן</p>
              </div>
            ) : (
              // Render the latest N notifications regardless of read state, visually
              // dimming the read ones — previously the list was filtered to unread only,
              // which meant a notification that arrived mid-read could be lost from view.
              notifications.map((notif) => {
                const createdAt = toDate(notif.createdAt);
                const isUnread = !notif.readBy?.[user.uid];
                return (
                  <div
                    key={notif.id}
                    onClick={() => {
                      void markAsRead(notif.id).catch((err) =>
                        console.error('Failed to mark notification as read:', err)
                      );
                    }}
                    className={`border-line relative flex cursor-pointer items-start gap-3 border-b px-4 py-3 transition-colors ${
                      isUnread ? 'bg-brand-soft/60 hover:bg-brand-soft' : 'hover:bg-surface-soft'
                    }`}
                  >
                    {/* Unread = one signal: soft bg + a 2px brand rail on the start edge */}
                    {isUnread && <span className="bg-brand absolute inset-y-3 right-0 w-0.5" />}
                    <div
                      className={`border-line bg-surface text-brand flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border ${
                        isUnread ? '' : 'opacity-60'
                      }`}
                    >
                      {notif.type === 'broadcast' ? <Megaphone size={14} /> : <User size={14} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="border-line bg-surface text-muted rounded-badge shrink-0 border px-1.5 py-px text-[9px] font-bold tracking-wide uppercase">
                          {notif.type === 'broadcast' ? 'שידור' : 'אישי'}
                        </span>
                        <p
                          className={`truncate text-sm ${
                            isUnread ? 'text-ink font-bold' : 'text-muted font-medium'
                          }`}
                        >
                          {notif.title}
                        </p>
                      </div>
                      <p
                        className={`mt-0.5 line-clamp-2 text-xs ${
                          isUnread ? 'text-ink-2' : 'text-sub'
                        }`}
                      >
                        {notif.body}
                      </p>
                      {createdAt && (
                        <p className="text-muted mt-1 text-[11px] tabular-nums">
                          {format(createdAt, 'dd/MM HH:mm', { locale: he })}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
